import { Media, ProfileMedia, AuditionGroupMedia } from '@minecraft.graphql-operations';
import { gqlMedia, gqlProfileMedia, gqlAuditionGroupMedia } from '@minecraft.graphql-types';
import { DeepPartial } from '@minecraft.utils';
import { MediaAudioProps } from '../types';
import {
  getCloudinary,
  getMediaDownloadOriginalUrl,
  getMediaNameWithExtension,
  sanitizeDownloadFileName,
} from './mediaHookUtils';

export const formatMediaToAudio = <T extends DeepPartial<gqlMedia> | DeepPartial<Media>>({
  media,
}: {
  media: T;
}): MediaAudioProps => {
  const cld = getCloudinary({ code: media?.mediaStorageStatus?.code });
  const fileKey = media?.fileKey;
  const mediaName = media?.name;
  const mediaNameWithExt = getMediaNameWithExtension({ media });
  /** cloudinary-core only ever inserts a "v1" version segment into the URL when the public ID
   * (fileKey) contains a "/" - real fileKeys always have a folder prefix (e.g. "stg/<guid>") and
   * get "v1", flat fileKeys (only seen in tests) never do, regardless of force_version. CloudinaryVideoAudio
   * (the legacy audio/video player still used elsewhere) never sets force_version either, so it gets the
   * same "v1"-for-real-fileKeys behavior from the library default. Matching that here means our src
   * resolves to the exact asset Cloudinary already has cached/derived for that fileKey, instead of a new one. */
  const doesFileKeyIncludeFolderPath = fileKey?.includes('/');
  const src = cld.video_url(fileKey, {
    audioCodec: 'mp3',
    fetchFormat: 'mp3',
    quality: 'auto',
    // eslint-disable-next-line camelcase
    force_version: doesFileKeyIncludeFolderPath,
  });
  const downloadSrc = cld.video_url(fileKey, {
    // eslint-disable-next-line camelcase
    force_version: false,
    flags: `attachment:${sanitizeDownloadFileName(mediaName)}`,
  });

  return {
    src,
    downloadUrl: getMediaDownloadOriginalUrl({ primaryUrl: downloadSrc }),
    name: mediaName,
    nameWithExt: mediaNameWithExt,
  };
};

export const formatProfileMediaToAudio = <T extends DeepPartial<gqlProfileMedia> | DeepPartial<ProfileMedia>>({
  profileMedia,
}: {
  profileMedia: T;
}): MediaAudioProps => {
  return formatMediaToAudio({ media: profileMedia?.media });
};

export const formatAuditionGroupMediaToAudio = <
  T extends DeepPartial<gqlAuditionGroupMedia> | DeepPartial<AuditionGroupMedia>
>({
  groupMedia,
}: {
  /** the audition group media object from the network query */
  groupMedia: T;
}): MediaAudioProps => {
  const media = groupMedia?.media;
  const name = groupMedia?.name || media?.name;
  return formatMediaToAudio({ media: { ...media, name } });
};
